// Sidebar — driven by TOOL_REGISTRY plus an admin section. Tools the user
// hasn't been granted access to are filtered out (server returns the
// permission list in /api/auth/me). Admin links only render for ToolsHub.Admin.
import { NavLink } from "react-router";
import {
  Home,
  Wrench,
  Target,
  Building2,
  CalendarClock,
  Users,
  BarChart3,
  LayoutDashboard,
  Scissors,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";
import { TOOL_REGISTRY } from "../../../../shared/tools/registry";
import { useAuth } from "../../auth/useAuth";

const ICONS: Record<string, LucideIcon> = {
  Home,
  Wrench,
  Target,
  Building2,
  CalendarClock,
  Users,
  BarChart3,
  LayoutDashboard,
  Scissors,
  Settings: SettingsIcon,
};

function Item({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
}) {
  return (
    <NavLink to={to} end className="bsp-sb-item">
      <Icon size={16} />
      <span>{label}</span>
    </NavLink>
  );
}

export function Sidebar() {
  const { user, isAdmin } = useAuth();
  const granted = new Set(user?.toolPermissions ?? []);
  // Permission-driven: admin sees every registered tool; everyone else sees
  // only the tools they've been granted via tool_permissions. Status (active
  // / beta / coming-soon) drives visual treatment in tile components, not
  // sidebar visibility.
  const tools = TOOL_REGISTRY.filter((t) => isAdmin || granted.has(t.id));

  return (
    <aside className="bsp-sidebar">
      <div className="bsp-sb-logo">
        <img
          src="/assets/bsp-logo-eng.png"
          alt="BSP Engineering"
          className="bsp-sb-logo-img"
        />
      </div>

      <nav>
        <div className="bsp-sb-section">Hub</div>
        <Item to="/" icon={Home} label="Dashboard" />

        <div className="bsp-sb-section">Tools</div>
        {tools.map((tool) => {
          const Icon = ICONS[tool.icon] ?? Wrench;
          return (
            <Item key={tool.id} to={tool.route} icon={Icon} label={tool.name} />
          );
        })}

        {isAdmin && (
          <>
            <div className="bsp-sb-section">Admin</div>
            <Item to="/admin/users" icon={Users} label="User management" />
            <Item to="/admin/analytics" icon={BarChart3} label="Analytics" />
            <Item to="/admin/settings" icon={SettingsIcon} label="Settings" />
          </>
        )}
      </nav>
    </aside>
  );
}
